import Card from '@/components/card';
import FormSection from '@/components/form/form-section';
import PageLink from '@/components/page-link';
import Text from '@/components/text';
import { ClassifierSubType } from '@/lib/models/classifier';
import {
  ClassifierControlsSettings,
  getClassifierControlActionOptions,
} from '@/lib/models/policy-model-v1/classifier-controls';
import { ControlActionValue } from '@/lib/models/policy-model-v1/controls';
import { FC, useCallback, useMemo } from 'react';
import { getFormattedUiOptions } from '../../select/helpers';
import {
  ClassifierControlListProps,
  filterUniqueEntities,
  updateClassifierAction,
} from './helpers';
import { Item } from './item';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const IntentControlList: FC<ClassifierControlListProps> = ({
  formHandle,
  orgPolicy,
  policy,
  readonly,
  classifiers,
  refetch,
}) => {
  const { values, setFieldValue } = formHandle;
  const { classifierControls } = values;
  const { entities = [] } = classifierControls;
  const orgClassifierControls = orgPolicy?.classifierControls;

  const intentClassifiers = useMemo(
    () => classifiers.filter(({ subtype }) => subtype === ClassifierSubType.Custom),
    [classifiers]
  );
  const hasClassifiers = !!intentClassifiers.length;

  const handleChange = useCallback(
    (classifierId: string, action: ControlActionValue) => {
      const updated = updateClassifierAction(entities, classifierId, action);
      setFieldValue(
        'classifierControls.entities',
        filterUniqueEntities(updated, orgClassifierControls?.entities)
      );
    },
    [entities, orgClassifierControls?.entities, setFieldValue]
  );

  const actionOptionsMap = useMemo(
    () => getClassifierControlActionOptions(intentClassifiers, policy, orgPolicy),
    [intentClassifiers, policy, orgPolicy]
  );

  const classifierSettings = useMemo(
    () => ClassifierControlsSettings(intentClassifiers),
    [intentClassifiers]
  );

  return (
    <FormSection title="Intent Topic Controls" scrollId="intent-controls">
      <FormSectionSubtitle>
        Choose what action is taken when a request matches an intent topic. Head over to{' '}
        <PageLink routeKey="classifiers" /> to create or manage intent topics.
      </FormSectionSubtitle>
      <Card>
        {!hasClassifiers ? (
          <Text italic>No intent topics found.</Text>
        ) : (
          classifierSettings.map(({ classifierId, label, description, enabled, defaultAction }) => {
            const entity = entities.find(({ classifierId: id }) => id === classifierId);
            const orgEntity = orgClassifierControls?.entities?.find(
              ({ classifierId: id }) => id === classifierId
            );
            const options = getFormattedUiOptions(actionOptionsMap.get(classifierId) || []);

            return (
              <Item
                key={`classifier-${classifierId}`}
                classifierId={classifierId}
                label={label}
                description={description}
                enabled={!!enabled}
                readonly={readonly}
                name={`classifierControls.${classifierId}`}
                options={options}
                value={entity?.action || orgEntity?.action || defaultAction}
                refetch={refetch}
                onChange={(value: string) =>
                  handleChange(classifierId, value as ControlActionValue)
                }
              />
            );
          })
        )}
      </Card>
    </FormSection>
  );
};

export default IntentControlList;
